<template>
  <div class="page-layout">
    <div class="page-content">
      <AppBreadcrumbs :items="breadcrumbItems" />
      <div class="page-header-title">
        <UserAvatar v-if="author" :user="author" size="md" />
        <h1>{{ pageTitle }}</h1>
      </div>

      <div v-if="loading" class="panel">
        <p class="empty-state">Загрузка...</p>
      </div>

      <div v-else-if="error" class="panel">
        <p class="error-message">{{ error }}</p>
      </div>

      <div v-else class="panel">
        <EventList
          :events="events"
          empty-text="У автора пока нет публичных событий."
          @select="openEvent"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import EventList from '../components/EventList.vue'
import UserAvatar from '../components/UserAvatar.vue'
import AppBreadcrumbs from '../components/AppBreadcrumbs.vue'
import { fetchUserPublicEvents, fetchUserPublicEventsByUsername } from '../api/events'
import { userDisplayName } from '../utils/userDisplay'
import { redirectToLoginIfUnauthorized } from '../utils/profileAccess'

const route = useRoute()
const router = useRouter()

const author = ref(null)
const events = ref([])
const loading = ref(true)
const error = ref('')

const pageTitle = computed(() => {
  if (author.value) {
    return `Публичные события — ${userDisplayName(author.value)}`
  }
  return 'Публичные события'
})

const breadcrumbItems = computed(() => {
  const items = [{ label: 'События', to: { name: 'events-home' } }]

  if (author.value) {
    if (author.value.username) {
      items.push({
        label: userDisplayName(author.value),
        to: { name: 'public-profile', params: { username: author.value.username } },
      })
    } else {
      items.push({ label: userDisplayName(author.value) })
    }
  }

  items.push({ label: 'Публичные события' })
  return items
})

async function loadAuthorEvents() {
  loading.value = true
  error.value = ''
  author.value = null
  events.value = []
  let redirecting = false

  try {
    const response = route.params.username
      ? await fetchUserPublicEventsByUsername(route.params.username)
      : await fetchUserPublicEvents(route.params.id)
    author.value = response.data.author
    events.value = response.data.events
  } catch (err) {
    if (redirectToLoginIfUnauthorized(err, router, route)) {
      redirecting = true
      return
    }

    if (err.response?.status === 404) {
      error.value = route.params.username ? 'Профиль не найден или скрыт' : 'Автор не найден'
    } else {
      error.value = 'Не удалось загрузить события автора'
    }
  } finally {
    if (!redirecting) {
      loading.value = false
    }
  }
}

function openEvent(id) {
  router.push({ name: 'event', params: { id } })
}

onMounted(loadAuthorEvents)
watch(() => [route.params.id, route.params.username], loadAuthorEvents)
</script>
